import { isStaticCardioZone } from '../../config/zone-mappings';
import { AreaCategory } from '../../config';
import { AudioSoundEvent, Exercise, Phase, SessionSequence,
  SessionSequenceEntry, WorkoutSettings } from '../../entities';
import { Delays } from '../../entities/exercise/delays';
import {
  IExerciseCompletionPercentageIndicator,
  ISessionSequenceInfo } from '../../entities/workoutsession/SessionSequenceInfo';

export const GetCurrentSessionSequenceIndex = (
    sessionSequence: SessionSequenceEntry[],
    remainingSeconds: number): number => {
  const currentSessionSequenceIndex = sessionSequence.findIndex(
    (sessionSequenceEntry: SessionSequenceEntry, index: number) => {
    const totalDurationRemaining = CalculateTotalDurationRemainingFromSequenceIndex(sessionSequence, index);
    return remainingSeconds > totalDurationRemaining - sessionSequenceEntry.durationInMilliseconds;
  });

  return currentSessionSequenceIndex;
};

export const GetCurrentSessionSequenceInRange = (sessionSequence: SessionSequenceEntry[], remainingSeconds: number):
  SessionSequenceEntry => {
  return sessionSequence[GetCurrentSessionSequenceIndex(sessionSequence, remainingSeconds)];
};

export const CalculateTotalDurationRemainingFromSequenceIndex = (
  sessionSequence: SessionSequenceEntry[],
  index: number): number => {
  const remainingSessionSequence = sessionSequence.slice(index, sessionSequence.length);
  return remainingSessionSequence.reduce((totalDuration: number, sessionSequenceEntry: SessionSequenceEntry) => {
    return totalDuration + sessionSequenceEntry.durationInMilliseconds;
  }, 0);
};

export const CalculateTotalDurationCompletedUntilEndOfSequenceIndex = (
  sessionSequence: SessionSequenceEntry[],
  sequenceIndex: number): number => {
  const endOfSequenceIndex = sequenceIndex + 1;
  const completedSessionSequence = sessionSequence.slice(0, endOfSequenceIndex);
  return completedSessionSequence.reduce((totalDuration: number, sessionSequenceEntry: SessionSequenceEntry) => {
    return totalDuration + sessionSequenceEntry.durationInMilliseconds;
  }, 0);
};

export const CalculateTotalDurationLeftFromSequence = (
  sessionSequence: SessionSequenceEntry[],
  remainingSeconds: number): number => {
  const index = GetCurrentSessionSequenceIndex(sessionSequence, remainingSeconds);
  const remainingSessionSequence = sessionSequence.slice(index, sessionSequence.length);
  return remainingSessionSequence.reduce((totalDuration: number, sessionSequenceEntry: SessionSequenceEntry) => {
    return totalDuration + sessionSequenceEntry.durationInMilliseconds;
  }, 0);
};

export const CalculateRemainingSecondsInPhase = (
  sequence: SessionSequenceEntry[],
  currentSequenceIndex: number,
  remainingSecondsInSequence: number,
): number => {
  const currentSessionSequenceEntry = sequence[currentSequenceIndex];
  let remainingSecondsInPhase = remainingSecondsInSequence;
  if (currentSequenceIndex === sequence.length - 1) {
    return remainingSecondsInPhase;
  }
  const currentPhase = currentSessionSequenceEntry.phase;
  const nextPhaseIndex = currentSequenceIndex + 1;
  for (let i = nextPhaseIndex; i < sequence.length; i++) {
    const followingPhase = sequence[i].phase;
    if (followingPhase === currentPhase) {
      remainingSecondsInPhase += sequence[i].durationInMilliseconds;
    } else {
      break;
    }
  }
  return remainingSecondsInPhase;
}

export const CalculateSecondsInSequence = (
  sequence: SessionSequenceEntry[],
  secondsRemainingInCycle: number,
): {
    secondsIntoCurrentSequence: number;
    secondsRemainingInCurrentSequence: number;
  } => {
  const currentSequenceEntryIndex = GetCurrentSessionSequenceIndex(sequence, secondsRemainingInCycle);
  const currentSequenceEntry = sequence[currentSequenceEntryIndex];

  const totalDurationOfCurrentAndRemainingSequencesInCycle = CalculateTotalDurationLeftFromSequence(
    sequence,
    secondsRemainingInCycle,
  );
  const secondsIntoCurrentSequence = totalDurationOfCurrentAndRemainingSequencesInCycle - secondsRemainingInCycle;
  const secondsRemainingInCurrentSequence = currentSequenceEntry.durationInMilliseconds - secondsIntoCurrentSequence;
  return { secondsIntoCurrentSequence, secondsRemainingInCurrentSequence };
}

export const CalculateMinutesRemainingInWorkout = (
  amountOfStages: number,
  currentStage: number,
  remainingSecondsInCurrentCycle: number,
  areaCategory: AreaCategory,
) => {
  const stagesRemaining = amountOfStages - currentStage;
  const midCycleMinute = remainingSecondsInCurrentCycle < 60 ? 0 : 1;
  const extraMinuteToRoundup = 1;
  let minutesRemainingCeiling = (stagesRemaining) * 2 + midCycleMinute + extraMinuteToRoundup;

  const isWorkoutCompletedBeforeTheLastMinute = isStaticCardioZone(areaCategory);
  if (isWorkoutCompletedBeforeTheLastMinute) {
    minutesRemainingCeiling = minutesRemainingCeiling - 1;
  }

  return minutesRemainingCeiling;
}

export const FlattenSequence = (sessionSequence: SessionSequence[]): SessionSequenceEntry[] => {
  if (!sessionSequence) {
    return [];
  }
  return sessionSequence.reduce((flattenedSequence: SessionSequenceEntry[], currentSequence: SessionSequence) => {
    return flattenedSequence.concat(currentSequence.sequence);
  }, []);
};

export const GetCombinedAudioSequenceForSessions = (
  sessionsSequences: SessionSequenceEntry[][],
  ): SessionSequenceEntry[] => {
  const timeMapOfAudioEvents = new Map<number, AudioSoundEvent>();
  sessionsSequences.forEach((session) => {
    session.forEach((sequence, sequenceIndex) => {
      if (sequence.audioSoundEvent == null) {
        sequence.audioSoundEvent = AudioSoundEvent.silent;
      }
      const durationAtEndOfSequence = CalculateTotalDurationCompletedUntilEndOfSequenceIndex(session, sequenceIndex);
      const isThereAConflictingAudioEvent = timeMapOfAudioEvents.get(durationAtEndOfSequence) != null;
      const skipDueToConflict = isThereAConflictingAudioEvent && sequence.audioSoundEvent === AudioSoundEvent.silent;
      if (!skipDueToConflict) {
        timeMapOfAudioEvents.set(durationAtEndOfSequence, sequence.audioSoundEvent);
      }
    });
  });

  const audioEventsEndTimes = Array.from(timeMapOfAudioEvents)
    .sort(([timeA], [timeB]) => timeA.valueOf() - timeB.valueOf())
    .map(([time, audioEvent]) => ({ time, audioEvent }));

  const audioSequence: SessionSequenceEntry[] = [];
  let endTimeOfPreviousSequenceEntry = 0;
  audioEventsEndTimes.forEach(({ time, audioEvent }) => {
    const startTimeOfThisSequencyEntry = endTimeOfPreviousSequenceEntry;
    const endTimeOfThisSequencyEntry = time.valueOf();
    const durationOfThisSequencyEntry = endTimeOfThisSequencyEntry - startTimeOfThisSequencyEntry;
    audioSequence.push({
      ...new SessionSequenceEntry(),
      durationInMilliseconds: durationOfThisSequencyEntry,
      audioSoundEvent: audioEvent,
    });
    endTimeOfPreviousSequenceEntry += durationOfThisSequencyEntry;
  });
  return audioSequence;
};

export const GetCurrentSequenceFrom = (
  endTime: Date,
  workoutDurationInMs: number,
  now: Date,
  sessionSequences: SessionSequenceEntry[],
) => {
  const remainingMillis = endTime.getTime() - now.getTime();
  const workoutDuration = workoutDurationInMs / 1000;
  return GetCurrentSequenceAndTimeRemaining(sessionSequences, remainingMillis, workoutDuration);
};

export const getLastTipDelay = (workDuration: number, lastTipCorrection: number) => {
  return workDuration * 1000 - lastTipCorrection;
};

export const getVoiceTipsDelays = (workDuration: number, firstTipDelay: number, midCorrection: number = 0) => {
  const midTip = (workDuration * 1000 / 2) - midCorrection;
  const lastTip = getLastTipDelay(workDuration, 10000);
  return new Delays(150, firstTipDelay, midTip, lastTip);
};

export const getInstructorVoiceTip = (exercise: Exercise, workoutSettings?: WorkoutSettings) => {
  return workoutSettings && workoutSettings.homeWorkoutInstructor ?
    `${exercise.uniqueReferenceNumber}_${workoutSettings.homeWorkoutInstructor}` : exercise.voiceTipFileName;
};

export const getTechniqueVoiceTipFilePath = (
  id: string, homeWorkoutInstructor: string) => {
  return `${id}_technique_${homeWorkoutInstructor}`;
};

export const getCorrectVoiceTipForSecondExercise =
  (exercise: Exercise, alternateExercise: Exercise, workoutSettings?: WorkoutSettings): string | undefined => {
    const isRepeatingExercise = Exercise.isRepeatingExercise(exercise, alternateExercise);

    if (workoutSettings) {
      return isRepeatingExercise ?
        getInstructorVoiceTip(exercise, workoutSettings) :
        getInstructorVoiceTip(alternateExercise, workoutSettings);
    } else {
      return isRepeatingExercise ? undefined : alternateExercise.voiceTipFileName;
    }
  };

export const getPositionToPlayVideoFrom = (
  currentSequence: SessionSequenceEntry | null,
  remainingSecondsInSequence: number | null): number => {
  let playFromPosition = 0;
  if (remainingSecondsInSequence && currentSequence && currentSequence.playVideoFromCurrentSequenceDuration) {
    const sequenceDuration = currentSequence.durationInMilliseconds;
    playFromPosition = (sequenceDuration - remainingSecondsInSequence) * 1000;
  }
  return playFromPosition;
};

export const getTimeInWorkout = (
  workoutTimeInformation: {
    workoutDurationInMs: number,
    endTime: Date,
  },
  now: Date): Date => {
  const remainingMillis = workoutTimeInformation.endTime.getTime() - now.getTime();
  const msIntoWorkout = workoutTimeInformation.workoutDurationInMs - remainingMillis;
  const zeroPaddedDateForTimeIntoWorkout = new Date(msIntoWorkout);
  return zeroPaddedDateForTimeIntoWorkout;
};

const GetCurrentSequenceAndTimeRemaining = (
  sessionSequences: SessionSequenceEntry[], msRemainingInWorkout: number, totalDurationOfWorkout: number) => {
  let totalRemainingSeconds = 0;
  const secondsRemainingInWorkout = Math.floor(msRemainingInWorkout / 1000);
  let sessionSequenceInfo: ISessionSequenceInfo | null = null;
  let currentZone: number = -1;
  let zoneCompletionPercentages: IExerciseCompletionPercentageIndicator[] = [];
  for (let currentIndex = sessionSequences.length - 1; currentIndex >= 0; currentIndex--) {
    const currentSequence = sessionSequences[currentIndex];
    totalRemainingSeconds += currentSequence.durationInMilliseconds;

    if (currentSequence.zone !== currentZone && !sessionSequenceInfo) {
      currentZone = currentSequence.zone;
      zoneCompletionPercentages = [];
    }
    if (secondsRemainingInWorkout <= totalRemainingSeconds) {
      const remainingSecondsInSequence = currentSequence.durationInMilliseconds -
        (totalRemainingSeconds - secondsRemainingInWorkout);

      if (!sessionSequenceInfo) {
        sessionSequenceInfo = {
          currentIndex,
          currentSequence,
          remainingSecondsInSequence: getRemainingSecondsInSequence(remainingSecondsInSequence),
          percentageCompletion: getPercentageCompletion(
            currentSequence.durationInMilliseconds,
            remainingSecondsInSequence),
          workoutPercentageCompletion: getPercentageCompletion(totalDurationOfWorkout, secondsRemainingInWorkout),
          msRemainingInWorkout,
          minutesLeftInWorkout: getMinutesLeftInWorkout(secondsRemainingInWorkout),
          interpolatedProgressText: getInterpolateProgressText(
            currentSequence.progressText,
            getRemainingSecondsInSequence(remainingSecondsInSequence),
          ),
          zoneCompletionPercentages: [],
        };
        pushZoneCompletionPercentage(currentSequence,
          zoneCompletionPercentages, sessionSequenceInfo.percentageCompletion);
      } else if (currentSequence.zone === currentZone) {
        pushZoneCompletionPercentage(currentSequence, zoneCompletionPercentages, 100);
      }
    } else {
      pushZoneCompletionPercentage(currentSequence, zoneCompletionPercentages, 0);
    }
  }

  if (sessionSequenceInfo) {
    sessionSequenceInfo.zoneCompletionPercentages = zoneCompletionPercentages;
  }
  return sessionSequenceInfo;
};

const pushZoneCompletionPercentage = (
  currentSequence: SessionSequenceEntry,
  zoneCompletionPercentages: IExerciseCompletionPercentageIndicator[],
  percentage: number,
) => {
  if (currentSequence.phase === Phase.Train || currentSequence.phase === Phase.Rest) {
    zoneCompletionPercentages.unshift({
      percentage,
      exerciseIndicatorLetter: currentSequence.exerciseIndicatorLetter,
    });
  }
};

const getInterpolateProgressText = (progressText: string, remainingSeconds: number) => {
  if (remainingSeconds >= 180) {
    const remainingMinutes = Math.floor(remainingSeconds / 60);
    return progressText.replace('{seconds}', `${remainingMinutes.toString()} minutes`);
  }
  return progressText.replace('{seconds}',
    `${remainingSeconds.toString()} ${remainingSeconds === 1 ? 'second' : 'seconds'}`);
};

const getPercentageCompletion = (duration: number, remainingSecondsInSequence: number) => {
  const percentageCompletion = (duration - remainingSecondsInSequence) * (100 / duration);
  return percentageCompletion > 100 ? 100 : Math.round(percentageCompletion);
};

const getMinutesLeftInWorkout = (secondsRemainingInWorkout: number) => {
  const minutesLeftInWorkout = Math.round(secondsRemainingInWorkout / 60);
  return minutesLeftInWorkout < 0 ? 0 : minutesLeftInWorkout;
};

const getRemainingSecondsInSequence = (secondsRemainingInSequence: number) => {
  return secondsRemainingInSequence < 0 ? 0 : secondsRemainingInSequence;
};
